import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
// [custom] Needed for cart re-sync. Both are already exported and used by component-cart-items.js.
import { sectionRenderer, morphSection } from '@theme/section-renderer';

/**
 * [custom] AJAX Cart API mutation endpoints. Not anchored at the start so Markets locale
 * prefixes (e.g. /fr/cart/add.js) still match.
 */
const CART_MUTATION_PATH = /\/cart\/(?:add|change|update|clear)(?:\.js|\.json)?$/;

/** [custom] Coalesces bursts of external requests (e.g. an app doing add then change) into one refresh. */
const EXTERNAL_REFRESH_DELAY_MS = 100;

/** [custom] How long a settled mutation window is kept for attributing late resource entries. */
const MUTATION_WINDOW_RETENTION_MS = 30000;

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, the installments CTA close-on-click and (custom) server re-sync.
 *
 * [custom] Cart re-sync
 * Apps that mutate the cart with raw AJAX Cart API calls (e.g. CartBot adding a gift) don't
 * dispatch standard events, so the drawer and header count go stale. This component re-renders
 * the drawer section from the server when:
 *   1. the drawer opens, and
 *   2. a PerformanceObserver sees a completed /cart/{add,change,update,clear} request that the
 *      theme did not announce via a standard cart event (i.e. it came from an app).
 * No monkey-patching of fetch/XHR is involved.
 *
 * `refresh()` is public so an app can also call it directly:
 *   document.querySelector('cart-drawer-component')?.refresh();
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /**
   * [custom] Upper bound on how long a refresh waits for in-flight cart mutations to settle.
   * Guards against a dispatcher that never resolves its event promise.
   * @type {number}
   */
  #mutationWaitTimeoutMs = 10000;

  /**
   * [custom] Settled-state wrappers for announced cart mutations that haven't resolved yet.
   * A refresh must not fetch while one is in flight, or the response can predate the mutation.
   * @type {Set<Promise<void>>}
   */
  #pendingMutations = new Set();

  /**
   * [custom] Incremented whenever an announced cart mutation starts. A refresh discards its
   * response if this changed mid-fetch, because the theme's own handler renders newer markup.
   * @type {number}
   */
  #mutationGeneration = 0;

  /**
   * [custom] Time windows (performance.now() timebase) of announced cart mutations. Each theme
   * mutation makes exactly one AJAX cart request, so each window "claims" one matching resource
   * entry; any unclaimed cart request is external.
   * @type {Array<{ start: number, end: number, claimed: boolean }>}
   */
  #mutationWindows = [];

  /** @type {PerformanceObserver | null} */
  #resourceObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #externalRefreshTimer;

  /** @type {AbortController | null} */
  #refreshController = null;

  /**
   * [custom] CTAs that already have a close listener, so repeat opens don't stack listeners.
   * @type {WeakSet<Element>}
   */
  #boundInstallmentsCtas = new WeakSet();

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    // [custom] Discount and note updates also hit /cart/update.js; track them so they aren't
    // mistaken for external mutations.
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleAnnouncedCartMutation);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleAnnouncedCartMutation);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#observeCartRequests();

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleAnnouncedCartMutation);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleAnnouncedCartMutation);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // [custom] Tear down so nothing morphs a detached section (e.g. theme editor re-render).
    this.#resourceObserver?.disconnect();
    this.#resourceObserver = null;
    clearTimeout(this.#externalRefreshTimer);
    this.#refreshController?.abort();
    this.#refreshController = null;
  }

  /**
   * [custom] Re-renders the drawer's cart section from the server and syncs the header cart bubble.
   *
   * Safe to call at any time: it waits for in-flight announced mutations, supersedes any earlier
   * refresh, and discards its response if the cart changes while it is fetching.
   *
   * @returns {Promise<boolean>} `true` if fresh markup was applied; `false` if skipped,
   *   superseded or failed. Never rejects.
   */
  async refresh() {
    this.#refreshController?.abort();
    const controller = new AbortController();
    this.#refreshController = controller;

    try {
      await this.#waitForPendingMutations(controller.signal);
      if (controller.signal.aborted || !this.isConnected) return false;

      const cartItems = /** @type {HTMLElement | null} */ (this.querySelector('cart-items-component'));
      const sectionId = cartItems?.dataset.sectionId;
      if (!sectionId) return false;

      const generation = this.#mutationGeneration;

      // useCache = false: bypass the section renderer's HTML cache.
      const html = await sectionRenderer.getSectionHTML(sectionId, false, undefined, controller.signal);

      if (controller.signal.aborted || !this.isConnected) return false;
      if (generation !== this.#mutationGeneration) return false;

      // Mirror cart-items-component: hydration mode for the drawer, and inject the section
      // stylesheet when going empty -> non-empty because the summary markup is new.
      const wasEmpty = this.querySelector('[data-cart-drawer-empty]') !== null;
      await morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: wasEmpty });

      this.#syncCartBubble();
      requestAnimationFrame(() => this.#updateStickyState());
      this.#bindInstallmentsCta();

      return true;
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[cart-drawer] Refresh failed:', error);
      return false;
    } finally {
      if (this.#refreshController === controller) this.#refreshController = null;
    }
  }

  /**
   * Handles the theme-drawer opening — updates sticky state, wires up the installments CTA
   * and (custom) re-syncs the drawer with the server.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    this.#bindInstallmentsCta();

    // [custom] Fire and forget; refresh() handles its own errors.
    this.refresh();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    // [custom] Must run before the auto-open below: theme-drawer dispatches its open event
    // synchronously inside open(), and the refresh it triggers needs to see this mutation.
    this.#trackMutation(event.promise);

    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * [custom] Tracks discount/note updates, which also POST to /cart/update.js.
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #handleAnnouncedCartMutation = (event) => {
    this.#trackMutation(event.promise);
  };

  /**
   * [custom] Records an announced cart mutation so refreshes can avoid racing it and so its
   * network request isn't treated as external.
   * @param {Promise<unknown> | undefined} promise
   */
  #trackMutation(promise) {
    this.#mutationGeneration += 1;

    const record = { start: performance.now(), end: Number.POSITIVE_INFINITY, claimed: false };
    this.#mutationWindows.push(record);
    this.#pruneMutationWindows();

    /** @type {Promise<void>} */
    const settled = Promise.resolve(promise)
      .then(
        () => undefined,
        () => undefined
      )
      .finally(() => {
        record.end = performance.now();
        this.#pendingMutations.delete(settled);
      });

    this.#pendingMutations.add(settled);
  }

  #pruneMutationWindows() {
    const cutoff = performance.now() - MUTATION_WINDOW_RETENTION_MS;
    this.#mutationWindows = this.#mutationWindows.filter((record) => record.end > cutoff);
  }

  /**
   * [custom] Waits until no announced cart mutation is in flight (loops, because new ones can
   * start while waiting), bounded by `#mutationWaitTimeoutMs`.
   * @param {AbortSignal} signal
   */
  async #waitForPendingMutations(signal) {
    const deadline = Date.now() + this.#mutationWaitTimeoutMs;

    while (this.#pendingMutations.size > 0 && !signal.aborted) {
      const remaining = deadline - Date.now();
      if (remaining <= 0) {
        console.warn('[cart-drawer] Timed out waiting for cart mutations; refreshing anyway.');
        return;
      }

      await Promise.race([
        Promise.all(this.#pendingMutations),
        new Promise((resolve) => setTimeout(resolve, remaining)),
      ]);
    }
  }

  /**
   * [custom] Observes completed network requests. Resource Timing entries are delivered after the
   * response finishes, so the server-side cart is already updated when we react.
   */
  #observeCartRequests() {
    if (this.#resourceObserver) return;
    if (typeof PerformanceObserver === 'undefined') return;
    if (!PerformanceObserver.supportedEntryTypes?.includes('resource')) return;

    this.#resourceObserver = new PerformanceObserver(this.#handleResourceEntries);
    this.#resourceObserver.observe({ type: 'resource' });
  }

  /**
   * @param {PerformanceObserverEntryList} list
   */
  #handleResourceEntries = (list) => {
    let sawExternalMutation = false;

    const entries = /** @type {PerformanceResourceTiming[]} */ (list.getEntries()).sort(
      (a, b) => a.startTime - b.startTime
    );

    for (const entry of entries) {
      if (!this.#isCartMutationRequest(entry)) continue;

      // Theme code dispatches its standard event *before* calling fetch, so its own request
      // starts inside its window. Claim one request per window; anything left over is external.
      const owner = this.#mutationWindows.find(
        (record) => !record.claimed && entry.startTime >= record.start && entry.startTime <= record.end
      );

      if (owner) {
        owner.claimed = true;
      } else {
        sawExternalMutation = true;
      }
    }

    if (sawExternalMutation) {
      clearTimeout(this.#externalRefreshTimer);
      this.#externalRefreshTimer = setTimeout(() => this.refresh(), EXTERNAL_REFRESH_DELAY_MS);
    }
  };

  /**
   * @param {PerformanceResourceTiming} entry
   * @returns {boolean}
   */
  #isCartMutationRequest(entry) {
    if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') return false;

    try {
      const url = new URL(entry.name);
      return url.origin === window.location.origin && CART_MUTATION_PATH.test(url.pathname);
    } catch {
      return false;
    }
  }

  /**
   * [custom] Updates header cart bubbles from the freshly morphed markup, avoiding an extra
   * /cart.js request. `[ref="cartItemCount"]` only renders for a non-empty cart
   * (snippets/cart-products.liquid), so its absence means 0.
   */
  #syncCartBubble() {
    const countText = this.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
    const itemCount = countText ? Number.parseInt(countText, 10) || 0 : 0;

    for (const icon of document.querySelectorAll('cart-icon')) {
      const cartIcon = /** @type {any} */ (icon);
      if (typeof cartIcon.renderCartBubble !== 'function') continue;
      if (cartIcon.currentCartCount === itemCount) continue;

      // No animation: the bubble is usually behind the open drawer.
      cartIcon.renderCartBubble(itemCount, false);
    }
  }

  /**
   * Re-queried on every call so it survives cart content re-renders that
   * replace the shopify-payment-terms shadow root.
   */
  #bindInstallmentsCta() {
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      if (!cta || this.#boundInstallmentsCtas.has(cta)) return;

      this.#boundInstallmentsCtas.add(cta);
      cta.addEventListener('click', () => this.#themeDrawer?.close());
    });
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}